import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const domain = process.env.REACT_APP_DOMAIN_NAME;
const apiVersion3 = process.env.REACT_APP_API_VERSION3;
const baseUrl = `${domain}${apiVersion3}`;

function getAuthHeaders() {
  const token = localStorage.getItem('authorization_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function getUploadHeaders(uploadToken: string) {
  return {
    'X-Upload-Token': uploadToken,
  };
}

export interface ChunkedUploadProgress {
  phase: 'idle' | 'token' | 'init' | 'uploading' | 'completing' | 'done' | 'error';
  chunksUploaded: number;
  totalChunks: number;
  percent: number;
  error?: string;
}

export interface CompletePayload {
  listener_role_id?: number;
  listener_audio_id?: number;
  title?: string;
  upload_source?: string;
  user_selected_tag?: string;
  message_quality?: number;
  video_id?: string;
}

export interface ChunkedUploadResult {
  id: number;
  file_path: string;
  media_type: string | null;
  title: string;
  listener_role_id: number;
}

interface UseChunkedUploadReturn {
  upload: (file: File, completePayload: CompletePayload) => Promise<ChunkedUploadResult>;
  progress: ChunkedUploadProgress;
  cancel: () => void;
  isUploading: boolean;
}

const DEFAULT_PROGRESS: ChunkedUploadProgress = {
  phase: 'idle',
  chunksUploaded: 0,
  totalChunks: 0,
  percent: 0,
};

export function useChunkedUpload(): UseChunkedUploadReturn {
  const [progress, setProgress] = useState<ChunkedUploadProgress>(DEFAULT_PROGRESS);
  const cancelledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    abortControllerRef.current?.abort();
  }, []);

  /**
   * Shared logic: acquire token, init session, upload all chunks.
   * Returns { uploadToken headers, upload_id, totalChunks } so the
   * caller can hit the appropriate /complete variant.
   */
  const uploadChunks = useCallback(async (file: File, signal: AbortSignal) => {
    setProgress({ phase: 'token', chunksUploaded: 0, totalChunks: 0, percent: 0 });

    const tokenRes = await axios.post(`${baseUrl}/uploads/token`, {}, { headers: getAuthHeaders(), signal });
    const uploadToken = tokenRes.data.upload_token as string;
    const uploadHeaders = getUploadHeaders(uploadToken);

    setProgress({ phase: 'init', chunksUploaded: 0, totalChunks: 0, percent: 0 });

    const initRes = await axios.post(
      `${baseUrl}/uploads/init`,
      {
        content_type: file.type || 'video/mp4',
        total_size: file.size,
        total_chunks: Math.ceil(file.size / (10 * 1024 * 1024)),
      },
      { headers: { ...uploadHeaders, 'Content-Type': 'application/json' }, signal },
    );
    const { upload_id, chunk_size } = initRes.data as { upload_id: string; chunk_size: number };
    const totalChunks = Math.ceil(file.size / chunk_size);

    let alreadyReceived: number[] = [];
    try {
      const statusRes = await axios.get(`${baseUrl}/uploads/${upload_id}/status`, {
        headers: uploadHeaders,
        signal,
      });
      alreadyReceived = statusRes.data.received_chunks || [];
    } catch {
      // Fresh upload
    }

    setProgress({ phase: 'uploading', chunksUploaded: alreadyReceived.length, totalChunks, percent: 0 });
    let uploaded = alreadyReceived.length;

    for (let i = 0; i < totalChunks; i++) {
      if (cancelledRef.current) throw new Error('Upload cancelled');
      if (alreadyReceived.includes(i)) continue;

      const start = i * chunk_size;
      const end = Math.min(start + chunk_size, file.size);
      const formData = new FormData();
      formData.append('chunk', file.slice(start, end), `chunk_${i}`);

      await axios.put(`${baseUrl}/uploads/${upload_id}/chunks/${i}`, formData, {
        headers: { ...uploadHeaders, 'Content-Type': 'multipart/form-data' },
        signal,
      });

      uploaded++;
      setProgress({
        phase: 'uploading',
        chunksUploaded: uploaded,
        totalChunks,
        percent: Math.round((uploaded / totalChunks) * 100),
      });
    }

    return { uploadHeaders, upload_id, totalChunks };
  }, []);

  const upload = useCallback(
    async (file: File, completePayload: CompletePayload): Promise<ChunkedUploadResult> => {
      cancelledRef.current = false;
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const { uploadHeaders, upload_id, totalChunks } = await uploadChunks(file, signal);

        setProgress({ phase: 'completing', chunksUploaded: totalChunks, totalChunks, percent: 100 });

        const completeRes = await axios.post(`${baseUrl}/uploads/${upload_id}/complete`, completePayload, {
          headers: { ...uploadHeaders, 'Content-Type': 'application/json' },
          signal,
        });

        setProgress({ phase: 'done', chunksUploaded: totalChunks, totalChunks, percent: 100 });
        return completeRes.data as ChunkedUploadResult;
      } catch (err: any) {
        const errorMessage = cancelledRef.current
          ? 'Upload cancelled'
          : err?.response?.data?.description || err?.message || 'Upload failed';
        setProgress((prev) => ({ ...prev, phase: 'error', error: errorMessage }));
        throw new Error(errorMessage);
      }
    },
    [uploadChunks],
  );

  const isUploading = progress.phase !== 'idle' && progress.phase !== 'done' && progress.phase !== 'error';

  return { upload, progress, cancel, isUploading };
}
